import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBoolean } from 'react-use'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { Trash16 } from '@makyoapp/makyo-icon/icon'

import { useOrgRemove, useOrgTransferOwnership } from '@/resolvers/org/api'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { USER_ORG_ROUTE, USER_ROUTE } from '@/shared/constants'
import { OrgDeleteModal, OrgTransferModal } from '@/components/ListOrgCard'

import { ReactComponent as IconRefreshCw } from '@/assets/vectors/icon_lucide_refresh_cw.svg?react'

export default function OrganizationSection() {
  const [trfModal, setTrfModal] = useBoolean(false)
  const [delModal, setDelModal] = useBoolean(false)
  const { currentOrg } = useCurrentOrgContext()
  const navigate = useNavigate()

  const handleHideTransferCallback = useCallback(() => setTrfModal(false), [setTrfModal])
  const { handleTransfer, transferTarget, setTransferTarget, handleHideTransfer } = useOrgTransferOwnership(
    currentOrg,
    handleHideTransferCallback
  )

  const handleRmHideCallback = useCallback(() => setDelModal(false), [setDelModal])
  const { rmTransferTarget, setRmTransferTarget, handleRmHide, handleRm } = useOrgRemove(
    currentOrg,
    handleRmHideCallback
  )

  const canTransfer = useOrgPerm(OrgPermission.Transfer)
  const canRemove = useOrgPerm(OrgPermission.Remove)

  if (!currentOrg || (!canTransfer && !canRemove)) return null

  return (
    <>
      <div className='bg-white dark:bg-slate-800/50 rounded-xl p-6 backdrop-blur-xs border border-slate-200/50 dark:border-slate-700/50 shadow-xs'>
        <h2 className='text-lg font-medium text-slate-900 dark:text-white mb-6'>Organization</h2>
        <div className='grid grid-cols-2 gap-4'>
          {canTransfer && (
            <button
              onClick={() => setTrfModal(true)}
              className='p-3 bg-slate-100 dark:bg-slate-700/30 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700/50 transition-all text-left flex items-center gap-3 text-slate-900 dark:text-white'
            >
              <IconRefreshCw className='h-5 w-5 text-slate-600 dark:text-slate-400 fill-transparent' />
              <span>Transfer Ownership</span>
            </button>
          )}
          {canRemove && (
            <button
              onClick={() => setDelModal(true)}
              className='p-3 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 transition-all text-left flex items-center gap-3'
            >
              <Trash16 className='h-5 w-5' />
              <span>Remove Organization</span>
            </button>
          )}
        </div>
      </div>

      <OrgTransferModal
        id='org-welcome-transfer-modal'
        show={trfModal}
        onHide={handleHideTransfer}
        org={currentOrg}
        transferTo={transferTarget}
        setTransferTo={setTransferTarget}
        onSubmit={(done) => {
          handleTransfer().finally(done)
        }}
      />
      <OrgDeleteModal
        id='org-welcome-delete-modal'
        show={delModal}
        onHide={handleRmHide}
        org={currentOrg}
        transferTo={rmTransferTarget}
        setTransferTo={setRmTransferTarget}
        onSubmit={(done) => {
          handleRm()
            .then(() => {
              navigate(`${USER_ROUTE}/${USER_ORG_ROUTE}`)
            })
            .finally(done)
        }}
      />
    </>
  )
}
